import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Notification } from '../../interfaces/notification';
import { NotificationService } from '../../services/notification/notification.service';
import { RealEstateService } from '../../services/real-estate/real-estate.service';
import { initFlowbite } from 'flowbite';

@Component({
  selector: 'app-notification-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './notification-modal.component.html',
  styleUrl: './notification-modal.component.css'
})
export class NotificationModalComponent implements OnInit {
  @Input() notification: Notification | null = null;
  @Input() searchState: string | null = null;
  @Input() searchCity: { city: string; file: string } | null = null;
  @Input() searchType: string | string[] | null = null;
  @Input() searchDiscount: number | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() notificationSaved = new EventEmitter<Notification>();

  states: string[] = [];
  cities: string[] = [];
  realEstateTypes: string[] = [];

  newNotification: Notification = {
    id: '',
    title: '',
    state: '',
    city: '',
    type: [],
    discount: 0
  };

  selectedTypes: Map<string, boolean> = new Map();

  isEditing = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private notificationService: NotificationService,
    private realEstateService: RealEstateService
  ) {}

  ngOnInit(): void {
    initFlowbite();

    // Load states
    this.realEstateService.getStates().then(states => {
      this.states = states;
    });

    // Load real estate types
    this.realEstateService.getRealEstateTypes().subscribe(types => {
      this.realEstateTypes = types;

      // Initialize the selectedTypes map with all types set to false
      this.realEstateTypes.forEach(type => {
        this.selectedTypes.set(type, false);
      });

      this.applySelectedTypesToMap();
    });

    // If we have a notification, we're in edit mode
    if (this.notification) {
      this.isEditing = true;
      this.newNotification = { ...this.notification };

      // Handle type field which could be string or string[]
      if (typeof this.newNotification.type === 'string') {
        // Convert a single string to array for consistency
        const typeValue = this.newNotification.type;
        this.newNotification.type = typeValue ? [typeValue] : [];
      }

      this.applySelectedTypesToMap();

      // Load cities for the selected state when editing
      this.updateCities();
    }
    // If we have search parameters, pre-fill the form
    else if (this.searchState || this.searchCity || this.searchType || this.searchDiscount !== null) {
      if (this.searchState) {
        this.newNotification.state = this.searchState;
        this.updateCities();
      }
      if (this.searchCity) {
        this.newNotification.city = this.searchCity.city;
      }
      if (this.searchType) {
        if (typeof this.searchType === 'string') {
          this.newNotification.type = [this.searchType];
        } else if (Array.isArray(this.searchType)) {
          // mark all
          this.newNotification.type = this.searchType;
        }
        this.applySelectedTypesToMap();
      }
      if (this.searchDiscount !== null) {
        this.newNotification.discount = this.searchDiscount;
      }
    }
  }

  private applySelectedTypesToMap(): void {
    this.selectedTypes.forEach((_, type) => {
      this.selectedTypes.set(type, false);
    });

    if (Array.isArray(this.newNotification.type)) {
      this.newNotification.type.forEach(type => {
        this.selectedTypes.set(type, true);
      });
    }
  }

  updateCities(): void {
    if (this.newNotification.state) {
      this.realEstateService.getCities(this.newNotification.state).then(cities => {
        // Service now returns { city, file }[], but the notification modal expects string city names
        this.cities = cities.map(c => c.city);
      });
    } else {
      this.cities = [];
    }
  }

  saveNotification(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    if (this.isEditing && this.notification?.id) {
      // Update existing notification
      this.notificationService.updateNotification({
        ...this.newNotification,
        id: this.notification.id
      })
      .then(updatedNotification => {
        this.successMessage = 'Notificação atualizada com sucesso.';
        this.notificationSaved.emit(updatedNotification);
      })
      .catch(error => {
        this.errorMessage = 'Erro ao atualizar a notificação. Por favor, tente novamente.';
        console.error('Error updating notification:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
    } else {
      // Create new notification
      this.notificationService.createNotification(this.newNotification)
      .then(createdNotification => {
        this.successMessage = 'Alerta criada com sucesso.';
        this.notificationSaved.emit(createdNotification);
      })
      .catch(error => {
        this.errorMessage = 'Erro ao criar a notificação. Por favor, tente novamente.';
        console.error('Error creating notification:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
    }
  }

  togglePropertyType(type: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedTypes.set(type, isChecked);

    // Update the notification type array based on selected types
    this.updateSelectedTypes();
  }

  updateSelectedTypes(): void {
    // Convert the map to an array of selected types
    const selectedTypes: string[] = [];
    this.selectedTypes.forEach((isSelected, type) => {
      if (isSelected) {
        selectedTypes.push(type);
      }
    });

    this.newNotification.type = selectedTypes;
  }

  validateForm(): boolean {
    if (!this.newNotification.title.trim()) {
      this.errorMessage = 'O nome da alerta é obrigatório.';
      return false;
    }
    if (!this.newNotification.state) {
      this.errorMessage = 'O estado é obrigatório.';
      return false;
    }
    if (!this.newNotification.city) {
      this.errorMessage = 'A cidade é obrigatória.';
      return false;
    }
    // Validate discount if provided
    if (this.newNotification.discount != null) {
      const d = Number(this.newNotification.discount);
      if (Number.isNaN(d) || d < 0 || d > 100) {
        this.errorMessage = 'O desconto deve ser um número entre 0 e 100.';
        return false;
      }
      // Normalize to integer if user entered decimal like 10.5
      this.newNotification.discount = Math.round(d);
    }
    return true;
  }

  cancel(): void {
    this.closeModal.emit();
  }
}
